import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Loader2,
  LayoutGrid,
  GripVertical,
  Save
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import WidgetLibrary from '../components/WidgetLibrary';
import DashboardTodoSection from '../components/DashboardTodoSection';

// Import all widget components
import CalendarWidget from '../components/widgets/CalendarWidget';
import RemindersWidget from '../components/widgets/RemindersWidget';
import NotesWidget from '../components/NotesWidget';

// Dynamic widget imports
import QuickClockWidget from '../components/widgets/QuickClockWidget';
import MyTasksWidget from '../components/widgets/MyTasksWidget';
import MyRosterWidget from '../components/widgets/MyRosterWidget';
import MyPerformanceWidget from '../components/widgets/MyPerformanceWidget';
import TeamOverviewWidget from '../components/widgets/TeamOverviewWidget';
import PendingApprovalsWidget from '../components/widgets/PendingApprovalsWidget';
import WarehouseKpiWidget from '../components/widgets/WarehouseKpiWidget';
import DeliveryReportsWidget from '../components/widgets/DeliveryReportsWidget';
import LeaveBalanceWidget from '../components/widgets/LeaveBalanceWidget';
import StorePerformanceWidget from '../components/widgets/StorePerformanceWidget';
import ComplianceStatusWidget from '../components/widgets/ComplianceStatusWidget';
import IncidentsWidget from '../components/widgets/IncidentsWidget';
import TempLogsTodayWidget from '../components/widgets/TempLogsTodayWidget';
import MyTempLogsWidget from '../components/widgets/MyTempLogsWidget';
import MyStoreExpiryWidget from '../components/widgets/MyStoreExpiryWidget';
import MonthlyReviewsWidget from '../components/widgets/MonthlyReviewsWidget';
import SystemStatsWidget from '../components/widgets/SystemStatsWidget';
import AllStoresWidget from '../components/widgets/AllStoresWidget';
import BillingConfirmationsWidget from '../components/widgets/BillingConfirmationsWidget';
import ConnectWidget from '../components/widgets/ConnectWidget';
import ProjectsWidget from '../components/widgets/ProjectsWidget';
import MyChecklistWidget from '../components/widgets/MyChecklistWidget';import StaffAttendanceWidget from '../components/widgets/StaffAttendanceWidget';
import DriverLogbookWidget from '../components/widgets/DriverLogbookWidget';
import DriverAlertsWidget from '../components/widgets/DriverAlertsWidget';
import RecognitionWidget from '../components/widgets/RecognitionWidget';
import StaffScoreHero from '../components/widgets/StaffScoreHero';
import SkillsTrainingWidget from '../components/widgets/SkillsTrainingWidget';
import EquipmentMaintenanceWidget from '../components/widgets/EquipmentMaintenanceWidget';
import StoreAuditWidget from '../components/widgets/StoreAuditWidget';
import LaborCostWidget from '../components/widgets/LaborCostWidget';
import StoreRankingWidget from '../components/widgets/StoreRankingWidget';
import HRDashboardWidget from '../components/widgets/HRDashboardWidget';
import LeaderboardWidget from '../components/widgets/LeaderboardWidget';
import MyBadgesWidget from '../components/widgets/MyBadgesWidget';
import ShoutoutsWidget from '../components/widgets/ShoutoutsWidget';
import EntitiesOverviewWidget from '../components/widgets/EntitiesOverviewWidget';
import FinancialDeadlinesWidget from '../components/widgets/FinancialDeadlinesWidget';
import ReconciliationWidget from '../components/widgets/ReconciliationWidget';
import CompanyStructureWidget from '../components/widgets/CompanyStructureWidget';
import HighPriorityTasksWidget from '../components/widgets/HighPriorityTasksWidget';
import QuickAccessWidget from '../components/widgets/QuickAccessWidget';
import SalesProductSuggestionsWidget from '../components/widgets/SalesProductSuggestionsWidget';
import CampaignPerformanceWidget from '../components/widgets/CampaignPerformanceWidget';
import PromotionalCalendarWidget from '../components/widgets/PromotionalCalendarWidget';
import SocialMediaHubWidget from '../components/widgets/SocialMediaHubWidget';
import SelfClockWidget from '../components/SelfClockWidget';
import CustomerInsightsWidget from '../components/widgets/CustomerInsightsWidget';
import MarketingBudgetWidget from '../components/widgets/MarketingBudgetWidget';
import StorePromotionPerformanceWidget from '../components/widgets/StorePromotionPerformanceWidget';
import SupplierDealsWidget from '../components/widgets/SupplierDealsWidget';
import DriverTrackingWidget from '../components/widgets/DriverTrackingWidget';
import DriverStatusWidget from '../components/widgets/DriverStatusWidget';
import DeliveryConfirmationWidget from '../components/widgets/DeliveryConfirmationWidget';
import DriverPerformanceWidget from '../components/widgets/DriverPerformanceWidget';
import MeetingsWidget from '../components/widgets/MeetingsWidget';
import TasksIAssignedWidget from '../components/widgets/TasksIAssignedWidget';
import QuickActionWidget from '../components/QuickActionWidget';
import ChecklistComplianceWidget from '../components/widgets/ChecklistComplianceWidget';
import MyStoreChecklistWidget from '../components/widgets/MyStoreChecklistWidget';

// Widget component mapping
const WIDGET_COMPONENTS = {
  calendar: CalendarWidget,
  reminders: RemindersWidget,
  my_checklist: MyChecklistWidget,
  notes: NotesWidget,
  quick_clock: QuickClockWidget,
  my_tasks: MyTasksWidget,
  tasks_i_assigned: TasksIAssignedWidget,
  connect: ConnectWidget,
  projects: ProjectsWidget,
  my_roster: MyRosterWidget,
  my_performance: MyPerformanceWidget,
  team_overview: TeamOverviewWidget,
  pending_approvals: PendingApprovalsWidget,
  warehouse_kpi: WarehouseKpiWidget,
  delivery_reports: DeliveryReportsWidget,
  leave_balance: LeaveBalanceWidget,
  store_performance: StorePerformanceWidget,
  compliance_status: ComplianceStatusWidget,
  incidents: IncidentsWidget,
  temp_logs_today: TempLogsTodayWidget,
  my_temp_logs: MyTempLogsWidget,
  my_store_expiry: MyStoreExpiryWidget,
  monthly_reviews: MonthlyReviewsWidget,
  system_stats: SystemStatsWidget,
  all_stores: AllStoresWidget,
  billing_confirmations: BillingConfirmationsWidget,
  staff_attendance: StaffAttendanceWidget,
  driver_logbook: DriverLogbookWidget,
  driver_alerts: DriverAlertsWidget,
  recognition: RecognitionWidget,
  skills_training: SkillsTrainingWidget,
  equipment_maintenance: EquipmentMaintenanceWidget,
  store_audit: StoreAuditWidget,
  labor_cost: LaborCostWidget,
  store_ranking: StoreRankingWidget,
  hr_dashboard: HRDashboardWidget,
  leaderboard: LeaderboardWidget,
  my_badges: MyBadgesWidget,
  shoutouts: ShoutoutsWidget,
  entities_overview: EntitiesOverviewWidget,
  financial_deadlines: FinancialDeadlinesWidget,
  reconciliation: ReconciliationWidget,
  company_structure: CompanyStructureWidget,
  high_priority_tasks: HighPriorityTasksWidget,
  quick_access: QuickAccessWidget,
  sales_product_suggestions: SalesProductSuggestionsWidget,
  campaign_performance: CampaignPerformanceWidget,
  promotional_calendar: PromotionalCalendarWidget,
  social_media_hub: SocialMediaHubWidget,
  customer_insights: CustomerInsightsWidget,
  marketing_budget: MarketingBudgetWidget,
  store_promotion_performance: StorePromotionPerformanceWidget,
  supplier_deals: SupplierDealsWidget,
  driver_tracking: DriverTrackingWidget,
  driver_status: DriverStatusWidget,
  delivery_confirmation: DeliveryConfirmationWidget,
  driver_performance: () => <DriverPerformanceWidget isAdmin={true} />,
  my_performance_driver: () => <DriverPerformanceWidget isAdmin={false} />,
  meetings: MeetingsWidget,
  checklist_compliance: ChecklistComplianceWidget,
  my_store_checklist: MyStoreChecklistWidget,
};

// Sortable Widget Wrapper
function SortableWidget({ widget, children, isEditMode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id, disabled: !isEditMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative min-w-0 overflow-hidden">
      {isEditMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute -top-2 -left-2 z-10 p-1.5 bg-white rounded-full shadow-md border border-slate-200 cursor-grab active:cursor-grabbing hover:bg-slate-50"
          title="Drag to reorder"
        >
          <GripVertical className="h-4 w-4 text-slate-500" />
        </div>
      )}
      <div className={`min-w-0 ${isEditMode ? 'ring-2 ring-blue-200 ring-offset-2 rounded-lg' : ''}`}>
        {children}
      </div>
    </div>
  );
}

export default function CustomizableDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [widgetOrder, setWidgetOrder] = useState([]);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchDashboardConfig();
  }, []);

  useEffect(() => {
    if (config?.active_widgets) {
      setWidgetOrder(config.active_widgets.map(w => w.id));
    }
  }, [config]);

  const fetchDashboardConfig = async () => {
    try {
      const data = await apiRequest('/widgets/dashboard-config');
      setConfig(data);
    } catch (error) {
      console.error('Failed to load dashboard config:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleWidgetsChange = (newWidgets) => {
    fetchDashboardConfig();
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setWidgetOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setHasOrderChanged(true);
    }
  };

  const saveWidgetOrder = async () => {
    setSaving(true);
    try {
      await apiRequest('/widgets/my-preferences', {
        method: 'PUT',
        body: JSON.stringify({
          enabled_widgets: widgetOrder,
          widget_order: widgetOrder
        })
      });
      toast.success('Widget order saved');
      setHasOrderChanged(false);
      setIsEditMode(false);
      fetchDashboardConfig();
    } catch (error) {
      toast.error('Failed to save widget order');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setWidgetOrder(config?.active_widgets?.map(w => w.id) || []);
    setHasOrderChanged(false);
    setIsEditMode(false);
  };

  const getWidgetById = (id) => {
    return config?.active_widgets?.find(w => w.id === id);
  };

  const renderWidget = (widget) => {
    const WidgetComponent = WIDGET_COMPONENTS[widget.id];
    
    if (!WidgetComponent) {
      return (
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 h-full">
          <p className="font-medium text-slate-700">{widget.name}</p>
          <p className="text-sm text-slate-500">{widget.description}</p>
          {widget.expandable && widget.expand_path && (
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={() => navigate(widget.expand_path)}
            >
              Open
            </Button>
          )}
        </div>
      );
    }

    return (
      <WidgetComponent 
        expandable={widget.expandable}
        expandPath={widget.expand_path}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#DC2626]" />
      </div>
    );
  }

  const userName = user?.name || config?.user?.name || 'User';
  
  // Check if user is basic staff (not management/admin)
  const staffRoles = ['staff', 'store_staff', 'warehouse_staff', 'driver'];
  const isBasicStaff = staffRoles.includes(user?.role?.toLowerCase());
  
  // Split widgets into compulsory and optional
  const compulsoryWidgetIds = widgetOrder.filter(id => getWidgetById(id)?.compulsory);
  const optionalWidgetIds = widgetOrder.filter(id => !getWidgetById(id)?.compulsory);

  return (
    <div className="space-y-6 overflow-hidden" data-testid="customizable-dashboard">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Welcome back, {userName.split(' ')[0]}!
          </h1>
          <p className="text-slate-500">
            {new Date().toLocaleDateString('en-AU', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        <div className="flex gap-2">
          {isEditMode ? (
            <>
              <Button
                variant="outline"
                onClick={cancelEdit}
                data-testid="cancel-edit-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={saveWidgetOrder}
                disabled={!hasOrderChanged || saving}
                className="bg-[#DC2626] hover:bg-[#B91C1C]"
                data-testid="save-order-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Order
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setIsEditMode(true)}
                data-testid="reorder-widgets-btn"
              >
                <GripVertical className="h-4 w-4 mr-2" />
                Reorder
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowLibrary(true)}
                data-testid="customize-dashboard-btn"
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                Customize
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Staff Score Hero - Shows prominently for ALL users except CEO/MD */}
      <StaffScoreHero />

      {/* Push-permission prompt is now mounted globally in Layout.js so
          every authenticated page can recover users whose subscription was
          cleaned up — not just the dashboard. */}

      {/* Self Clock Widget - Marketing, Tech, Operations */}
      <SelfClockWidget userRole={user?.role} />

      {/* To-Do List Section - Always at the top */}
      <DashboardTodoSection />

      {/* Quick Action Widget - Fast access to key forms */}
      <QuickActionWidget />

      {/* Edit Mode Indicator */}
      {isEditMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
          <GripVertical className="h-5 w-5 text-blue-600" />
          <p className="text-sm text-blue-800">
            <strong>Reorder mode:</strong> Drag widgets using the grip handle to rearrange them. Click "Save Order" when done.
          </p>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        {/* Compulsory Widgets Row */}
        <SortableContext items={compulsoryWidgetIds} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 min-w-0 overflow-hidden">
            {compulsoryWidgetIds.map(id => {
              const widget = getWidgetById(id);
              if (!widget) return null;
              return (
                <SortableWidget key={id} widget={widget} isEditMode={isEditMode}>
                  {renderWidget(widget)}
                </SortableWidget>
              );
            })}
          </div>
        </SortableContext>

        {/* Optional Widgets Grid */}
        {optionalWidgetIds.length > 0 && (
          <SortableContext items={optionalWidgetIds} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 min-w-0 overflow-hidden">
              {optionalWidgetIds.map(id => {
                const widget = getWidgetById(id);
                if (!widget) return null;
                return (
                  <SortableWidget key={id} widget={widget} isEditMode={isEditMode}>
                    {renderWidget(widget)}
                  </SortableWidget>
                );
              })}
            </div>
          </SortableContext>
        )}
      </DndContext>

      {/* Empty State */}
      {optionalWidgetIds.length === 0 && !isEditMode && (
        <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <LayoutGrid className="h-12 w-12 mx-auto mb-3 text-slate-400" />
          <h3 className="font-medium text-slate-900">Add more widgets</h3>
          <p className="text-sm text-slate-500 mb-4">
            Customize your dashboard with widgets that matter to you
          </p>
          <Button
            variant="outline"
            onClick={() => setShowLibrary(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Browse Widget Library
          </Button>
        </div>
      )}

      {/* Widget Library Modal */}
      <WidgetLibrary
        open={showLibrary}
        onClose={() => setShowLibrary(false)}
        onWidgetsChange={handleWidgetsChange}
        currentWidgets={config?.preferences?.enabled_widgets || []}
      />
    </div>
  );
}
// Build timestamp: 1775454706
